/**
 * Locale data: the words that say "this is a thing you buy, and this is the aisle it is in".
 *
 * Authorised by product-concept §7.2, which names "this looks like a shopping item" as one of
 * the three pre-triage outputs, and RULINGS R23, which rules the guess implementable once R22
 * moved department names into this package. It is carried here on the precedent DEVIATIONS.md
 * D18 set for `./stopwords.ts`: a keyword table is LOCALE DATA, not a screen's business logic.
 *
 * WHY IT IS HERE AND NOT IN `apps/mobile/src/screens/sorting/proposals.ts`.
 *
 *   1. It is untranslatable anywhere else. "milk" and "llaeth" belong beside the two registers
 *      of every other string in the product, and a second locale is a new export in this file
 *      rather than an edit to a screen.
 *   2. It must survive literal mode. The department a guess proposes is rendered through
 *      `dept.*`, which is a real catalogue key with both registers, so the guess is a proposal
 *      about a NAMED THING rather than about an English noun the screen happened to type.
 *   3. The department ids it maps to are checked by the compiler: `ShoppingDepartment` is derived
 *      from the `dept.*` keys (see ./catalogue.ts), so a keyword pointing at an aisle that has
 *      no name cannot be committed.
 *
 * ============================================================================================
 * THE DESIGN, IN ONE SENTENCE: SILENCE IS THE DEFAULT, AND A WRONG GUESS COSTS FAR MORE THAN
 * A MISSED ONE.
 * ============================================================================================
 *
 * product-concept §7.2 objection 2 was accepted on the grounds that "autistic users in
 * particular will lose trust fast at the first wrong guess", and RULINGS R23 names the exact
 * failure it must not reproduce: the fixture's loop "Re-hang the shed door" against the
 * shopping item "Shed door hinge".
 *
 * The first version of this file tried to honour that by curating the WORD LIST — admitting only
 * nouns that "name a thing bought in a shop and name nothing else". `docs/reviews/copy-v2-critique.md`
 * showed by measurement that curating the list is not enough, because the failures do not come
 * from bad words, they come from a good word inside a sentence that is not about shopping:
 *
 *     "chicken feed"            -> meat-fish   (it is animal feed; `chicken` beat the bigram)
 *     "order oil for the tank"  -> cupboard    (that is heating oil, not cooking oil)
 *     "milk the goats"          -> dairy-eggs  (it is not a shopping item at all — it is a TASK)
 *
 * Persona A (product-concept §3) is a smallholder. Farm vocabulary is not an edge case for this
 * product, it is the design anchor's daily language, and §7.3's own worked walkaround example is
 * "gate latch… water trough leaking… order feed". So the matcher — not the word list — now
 * carries the design, in four rules, each with a test in ./__tests__ measuring it:
 *
 *   1. A TASK IS NOT AN ITEM. A line whose head word is an imperative verb ("milk the goats",
 *      "order oil for the tank", "fix the drinker") is a task ABOUT a thing, not a request to
 *      buy it. Suppressed outright. This one rule kills the worst class of failure, because the
 *      worst failures are not mis-filed items — they are ordinary loops mistaken for items.
 *   2. AMBIGUOUS NOUNS NEVER MAP ALONE. `oil`, `feed`, `seed`, `nuts`, `roll`, `stock`,
 *      `spirits`, `chips`, `chicken`, `bags`, `pepper`, `squash`, `tinned` mean different things
 *      in a barn and a supermarket. They are absent from the unigram table and reachable only
 *      through a bigram that disambiguates them ("olive oil", "chicken feed", "kitchen roll").
 *   3. A FARM/DIY VETO SET. A line containing `trough`, `twine`, `hinge`, `tank`, `ewe`, `gate`…
 *      is not a supermarket line, whatever else it contains. The veto runs before any match, so
 *      "Baler twine" is silent even though a farm shop really does sell it.
 *   4. BIGRAMS BEAT UNIGRAMS, AND DISAGREEMENT IS SILENCE. Two clean passes, bigrams first, so
 *      the precedence is a property of the algorithm rather than of the order words happen to
 *      appear in. Two departments matching one line is a shopping TRIP, and picking one of its
 *      aisles would be the confident wrong guess objection 2 is about.
 *
 * A missed guess costs the user one tap. A confident wrong guess costs the product its one
 * differentiator, and it is the sorting surface — the surface where all the AI-trust copy
 * lives — that pays. WHEN IN DOUBT, SAY NOTHING. A table that is silent 40% of the time and
 * never wrong is correct for this product; one that answers 90% of the time and is wrong 5% is
 * not, and no amount of confidence-band hedging in the UI buys that 5% back.
 *
 * `LOCAL_CEILING` in `@carn/core` still caps what a table on one phone may claim about itself
 * (RULINGS R44), and the binding is proposed as its own inference part with its own confidence
 * (R31). A match here is evidence, never a verdict.
 */

import type { ShoppingDepartment } from './catalogue';

/**
 * English (en-GB), keyed by the SINGULAR lowercase noun, or by a lowercase PHRASE of two or three
 * words (`kitchen roll`, `chicken feed`, `washing up liquid`) — see `departmentForLine`, which
 * joins adjacent words and looks the longest span up before any shorter one.
 *
 * `departmentForWord` handles a trailing plural `s` and casing, so "Eggs" and "eggs" and "egg"
 * all reach the same entry and the table stays readable.
 *
 * Before adding a word, ask the two questions rule 2 asks: does it name something else in a
 * barn, a workshop or a garden? And is it ever the HEAD of a sentence that is not about
 * shopping? If either answer is yes, it belongs in `AMBIGUOUS_UNIGRAMS_EN` with a bigram, or
 * nowhere.
 */
export const SHOPPING_WORDS_EN: ReadonlyMap<string, ShoppingDepartment> = new Map<string, ShoppingDepartment>([
  // Fruit and veg.
  ['apple', 'produce'],
  ['banana', 'produce'],
  ['carrot', 'produce'],
  ['cucumber', 'produce'],
  ['lemon', 'produce'],
  ['lettuce', 'produce'],
  ['mushroom', 'produce'],
  ['onion', 'produce'],
  ['orange', 'produce'],
  ['potato', 'produce'],
  ['salad', 'produce'],
  ['satsuma', 'produce'],
  ['spinach', 'produce'],
  ['tomato', 'produce'],
  // The two `-oes` plurals in the whole table, spelled out rather than reached by a stemmer.
  // `departmentForWord` strips exactly one trailing `s`, so "tomatoes" would otherwise become
  // "tomatoe" and miss — and adding an `-es` rule to the normaliser is the first step onto the
  // slope the header refuses ("parking" → "park"). Two literal keys cost nothing and lie about
  // nothing.
  ['potatoes', 'produce'],
  ['tomatoes', 'produce'],
  // Bigrams that rescue an ambiguous head (rule 2): a pepper is a vegetable or a spice, a
  // squash is a vegetable or a cordial.
  ['red pepper', 'produce'],
  ['green pepper', 'produce'],
  ['butternut squash', 'produce'],

  // Bakery. NOT `roll` — "kitchen roll" is household and "baler twine" territory has rolls of
  // wire and roofing felt in it. Reachable only as a bigram.
  ['bagel', 'bakery'],
  ['baguette', 'bakery'],
  ['bread', 'bakery'],
  ['bread roll', 'bakery'],
  ['crumpet', 'bakery'],
  ['loaf', 'bakery'],
  ['scone', 'bakery'],
  ['teacake', 'bakery'],

  // Meat and fish. NOT `chicken` (the birds in the run outnumber the ones in the fridge on a
  // smallholding) and NOT `lamb` (which is in the veto set — on this farm a lamb is an animal
  // before it is a chop). Both are reachable as bigrams.
  ['bacon', 'meat-fish'],
  ['beef', 'meat-fish'],
  ['chicken breast', 'meat-fish'],
  ['chicken thighs', 'meat-fish'],
  ['roast chicken', 'meat-fish'],
  ['haddock', 'meat-fish'],
  ['ham', 'meat-fish'],
  ['mackerel', 'meat-fish'],
  ['mince', 'meat-fish'],
  ['pork', 'meat-fish'],
  ['salmon', 'meat-fish'],
  ['sausage', 'meat-fish'],

  // Dairy and eggs. `milk` stays, because "Milk" on its own is the single most likely shopping
  // capture in the product's own §8.2 example — the head-verb rule, not the word list, is what
  // stops "Milk the goats".
  ['butter', 'dairy-eggs'],
  ['cheddar', 'dairy-eggs'],
  ['cheese', 'dairy-eggs'],
  ['cream', 'dairy-eggs'],
  ['double cream', 'dairy-eggs'],
  ['egg', 'dairy-eggs'],
  ['milk', 'dairy-eggs'],
  ['yoghurt', 'dairy-eggs'],
  ['yogurt', 'dairy-eggs'],

  // Chilled.
  ['coleslaw', 'chilled'],
  ['hummus', 'chilled'],
  ['quiche', 'chilled'],

  // Frozen.
  ['ice cream', 'frozen'],
  ['lolly', 'frozen'],
  ['oven chips', 'frozen'],
  ['fish fingers', 'frozen'],

  // Cupboard and dry goods. NOT `oil` — the critique's own example, "order oil for the tank",
  // is heating oil on a farm and it is the commoner sense in this user's life.
  ['beans', 'cupboard'],
  ['cereal', 'cupboard'],
  ['crisps', 'cupboard'],
  ['flour', 'cupboard'],
  ['honey', 'cupboard'],
  ['jam', 'cupboard'],
  ['ketchup', 'cupboard'],
  ['marmalade', 'cupboard'],
  ['mustard', 'cupboard'],
  ['noodles', 'cupboard'],
  ['oats', 'cupboard'],
  ['pasta', 'cupboard'],
  ['porridge', 'cupboard'],
  ['rice', 'cupboard'],
  ['salt', 'cupboard'],
  ['sugar', 'cupboard'],
  ['vinegar', 'cupboard'],
  ['black pepper', 'cupboard'],
  ['olive oil', 'cupboard'],
  ['sunflower oil', 'cupboard'],
  ['vegetable oil', 'cupboard'],
  ['rapeseed oil', 'cupboard'],
  ['cooking oil', 'cupboard'],
  ['peanut butter', 'cupboard'],
  ['stock cubes', 'cupboard'],
  ['tinned tomatoes', 'cupboard'],
  ['tinned beans', 'cupboard'],

  // Drinks.
  ['ale', 'drinks'],
  ['beer', 'drinks'],
  ['cider', 'drinks'],
  ['coffee', 'drinks'],
  ['cordial', 'drinks'],
  ['juice', 'drinks'],
  ['lemonade', 'drinks'],
  ['tea', 'drinks'],
  ['teabags', 'drinks'],
  ['tea bags', 'drinks'],
  ['wine', 'drinks'],
  ['orange juice', 'drinks'],
  ['orange squash', 'drinks'],
  ['blackcurrant squash', 'drinks'],
  ['bottled water', 'drinks'],
  ['sparkling water', 'drinks'],
  ['mineral water', 'drinks'],

  // Household. "bin bags" is product-concept §10.1's own worked example, so it had better work —
  // as a BIGRAM, because a bag on a smallholding is far more likely to hold feed than rubbish.
  ['bin bags', 'household'],
  ['bin liners', 'household'],
  ['bleach', 'household'],
  ['clingfilm', 'household'],
  ['detergent', 'household'],
  ['dishcloth', 'household'],
  ['foil', 'household'],
  ['kitchen roll', 'household'],
  ['matches', 'household'],
  ['sponges', 'household'],
  ['toilet roll', 'household'],
  // Three words, reached by the trigram pass. NOT `washing up` on its own, which is a chore.
  ['washing up liquid', 'household'],

  // Health and beauty.
  ['deodorant', 'health-beauty'],
  ['floss', 'health-beauty'],
  ['ibuprofen', 'health-beauty'],
  ['paracetamol', 'health-beauty'],
  ['plasters', 'health-beauty'],
  ['razor', 'health-beauty'],
  ['shampoo', 'health-beauty'],
  ['soap', 'health-beauty'],
  ['sunscreen', 'health-beauty'],
  ['sun cream', 'health-beauty'],
  ['shaving cream', 'health-beauty'],
  ['toothpaste', 'health-beauty'],

  // Pet and animal feed — R22's own department for exactly this ("Pet and animal feed"), and
  // where every bag of feed belongs. Almost all of it is bigrams, because the unigram half of
  // each pair ("feed", "nuts", "pellets", "seed", "litter") is a farm word or a verb on its own.
  ['bird seed', 'pet-animal'],
  ['cat food', 'pet-animal'],
  ['cat litter', 'pet-animal'],
  ['dog food', 'pet-animal'],
  ['layers pellets', 'pet-animal'],
  ['sheep nuts', 'pet-animal'],
  ['pig nuts', 'pet-animal'],
  ['chicken feed', 'pet-animal'],
  ['chicken pellets', 'pet-animal'],
  ['poultry feed', 'pet-animal'],
  ['horse feed', 'pet-animal'],
  ['dog treats', 'pet-animal'],
]);

/**
 * The word count of the longest phrase in the table, DERIVED rather than declared, so that
 * adding a four-word item makes the matcher look for four-word spans instead of silently
 * becoming an entry nothing can ever reach.
 */
export const LONGEST_PHRASE = Math.max(
  ...[...SHOPPING_WORDS_EN.keys()].map((key) => key.split(' ').length),
);

/**
 * Rule 2. Words that name one thing in a shop and a different thing in a barn, a workshop or a
 * garden. They are DELIBERATELY absent from `SHOPPING_WORDS_EN` as unigrams; each is reachable
 * only through a bigram above that says which sense is meant.
 *
 * This set is not consulted by the matcher — a word that is not in the table cannot match, so
 * the absence is the mechanism. It is exported because a list of eight silent decisions is
 * invisible otherwise, and because the test suite asserts none of these ever reappears as a
 * unigram key, which is the only thing that stops a future well-meaning edit re-opening the
 * exact hole the critique found.
 */
export const AMBIGUOUS_UNIGRAMS_EN: ReadonlySet<string> = new Set([
  // The critique's list.
  'oil', // cooking oil in a kitchen; heating oil in a tank; two-stroke in a shed
  'feed', // a sack in a shop; a job at seven in the morning
  'seed', // bird seed; grass seed; seed potatoes
  'nuts', // cashews; sheep nuts; the things that go on bolts
  'roll', // a bread roll; kitchen roll; a roll of wire
  'stock', // stock cubes; livestock
  'spirits', // the drinks aisle; white spirit
  'chips', // the freezer; wood chips; bark chippings
  // Found by the same reasoning while rebuilding the table.
  'chicken', // the fridge, or the twelve of them in the run
  'bags', // bin bags; feed bags; sand bags
  'pepper', // a vegetable, or a spice
  'squash', // a vegetable, or a cordial
  'tinned', // an adjective, and it disagrees with whatever it modifies
  'pellets', // layers pellets; wood pellets
  'litter', // cat litter; litter picking
  'water', // a bottle of it, or the verb for what you do to a trough
]);

/**
 * Rule 1. Imperative verbs. A captured line that OPENS with one of these is a task about a
 * thing, not a request to buy it — "Order oil for the tank", "Fix the drinker", "Call the vet
 * about the ram". The single highest-value rule in the file: every one of the three failures the
 * critique found was a task, not a mis-filed item.
 *
 * Only the HEAD position is tested, and only when the line has more than one word, because a
 * one-word line is a noun by construction ("Milk") and a verb elsewhere in the line is usually
 * a modifier of a real item ("washing up liquid", "shaving cream").
 *
 * `buy`, `get`, `pick`, `grab` and `fetch` are deliberately NOT here: "Buy milk" is a shopping
 * line and its head verb is the strongest signal it is one.
 */
const TASK_VERBS_EN: ReadonlySet<string> = new Set([
  'order',
  'reorder',
  'fix',
  'mend',
  'repair',
  'replace',
  'service',
  'call',
  'ring',
  'phone',
  'email',
  'text',
  'chase',
  'check',
  'inspect',
  'book',
  'arrange',
  'sort',
  'tidy',
  'clear',
  'clean',
  'muck',
  'wash',
  'sweep',
  'empty',
  'collect',
  'deliver',
  'drop',
  'move',
  'shift',
  'hang',
  'rehang',
  'roof',
  'reroof',
  'reseed',
  'paint',
  'sand',
  'drill',
  'cut',
  'trim',
  'prune',
  'mow',
  'strim',
  'dig',
  'plant',
  'sow',
  'weed',
  'harvest',
  'learn',
  'practise',
  'practice',
  'read',
  'write',
  'send',
  'post',
  'pay',
  'file',
  'renew',
  'cancel',
  'return',
  'ask',
  'tell',
  'remind',
  'sharpen',
  'grease',
  'oil',
  'weigh',
  'worm',
  'drench',
  'shear',
  'clip',
  'treat',
  'sell',
  'load',
  'unload',
  'stack',
  'split',
  'fell',
  'burn',
  'refit',
  'rebuild',
  'top',
]);

/**
 * Rule 1, second half. Words that are BOTH a shop noun and an imperative verb. They match
 * normally anywhere in a line, but at the head of a line with more words after it they are read
 * as the verb and the line is suppressed.
 *
 * `milk` is the whole reason this set exists: "Milk" must reach the dairy aisle and "Milk the
 * goats" must reach nothing, and no word list can tell those apart — only position can.
 *
 * The set is kept SHORT on purpose. Every word added to it turns a legitimate two-word item into
 * a miss ("Butter, 250g" would be suppressed), so a word earns its place here only if it is a
 * plausible head of a smallholding sentence — which "butter" and "ham" are not.
 */
const DUAL_HEAD_VERBS_EN: ReadonlySet<string> = new Set([
  'milk', // "Milk the goats" — the critique's third failure, and the reason for this set
  'feed', // "Feed the hens"
  'water', // "Water the polytunnel"
  'stock', // "Stock up the log store"
  'dip', // "Dip the ewes"
  'bed', // "Bed the calves down"
  'roll', // "Roll the top field"
  'plaster', // "Plaster the back wall"
]);

/**
 * Rule 3. The farm and DIY veto. A line containing any of these is not a supermarket line,
 * whatever else it contains, and the veto runs BEFORE any matching.
 *
 * This deliberately loses real farm-shop items — a farm shop does sell baler twine, ewe nuts and
 * lamb — and the critique's minimum bar requires exactly that: "baler twine" must be silent. The
 * asymmetry is the point. Suppressing a line the user then files by hand costs one tap;
 * proposing "Meat and fish" for a lamb that is standing in a field costs the trust the whole
 * sorting surface is built on (product-concept §7.2 objection 2).
 *
 * The first twenty are the set the remediation brief names. The rest were added by the same
 * test: does this word appear in a supermarket aisle? If there is any doubt at all, it is in.
 */
const FARM_DIY_VETO_EN: ReadonlySet<string> = new Set([
  // The brief's set.
  'trough',
  'drinker',
  'twine',
  'baler',
  'hinge',
  'bolt',
  'tank',
  'ram',
  'ewe',
  'lamb',
  'hurdle',
  'wormer',
  'dip',
  'bedding',
  'straw',
  'silage',
  'fencing',
  'latch',
  'gate',
  'post',
  // Structures, ground and boundaries.
  'fence',
  'shed',
  'barn',
  'byre',
  'sty',
  'stable',
  'coop',
  'paddock',
  'pen',
  'yard',
  'roof',
  'gutter',
  'guttering',
  'ditch',
  'culvert',
  'hedge',
  'drain',
  'soakaway',
  'lintel',
  'joist',
  'rafter',
  'batten',
  'felt',
  'cladding',
  // Fixings, tools and materials. NOT `nut`, deliberately: `bolt` already vetoes the
  // "nuts and bolts" line, and vetoing `nut` would silence "Sheep nuts", which the remediation
  // brief requires to reach `pet-animal`. `nuts` is handled as an ambiguous unigram instead.
  'screw',
  'nail',
  'washer',
  'bracket',
  'wire',
  'netting',
  'mesh',
  'timber',
  'plank',
  'plywood',
  'cement',
  'concrete',
  'mortar',
  'grout',
  'sealant',
  'filler',
  'primer',
  'varnish',
  'creosote',
  'diesel',
  'kerosene',
  'paraffin',
  'strimmer',
  'chainsaw',
  'mower',
  'spanner',
  'chisel',
  'hacksaw',
  'crowbar',
  'trailer',
  'hitch',
  'tractor',
  'quad',
  'tack',
  'harness',
  'headcollar',
  // Livestock and husbandry.
  'heifer',
  'bullock',
  'cattle',
  'sow',
  'piglet',
  'weaner',
  'calf',
  'foal',
  'cockerel',
  'alpaca',
  'fleece',
  'dung',
  'slurry',
  'manure',
  'muck',
  'hoof',
  'udder',
  'teat',
  'haylage',
  'hay',
  'lick',
  'vet',
  'farrier',
  'shearer',
]);

/** The locale-appropriate table. One locale today; the signature is where a second lands. */
export function shoppingWords(locale = 'en'): ReadonlyMap<string, ShoppingDepartment> {
  void locale;
  return SHOPPING_WORDS_EN;
}

/**
 * Lowercase, no surrounding punctuation. Exported so a caller can report the word it matched
 * without a comma stuck to it.
 */
function bareWord(word: string): string {
  return word.toLowerCase().replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, '');
}

/**
 * Every non-alphanumeric character removed, so "Re-hang" and "rehang" are one verb. Used only
 * for the rule-1 head test — never for a table lookup, where a hyphen genuinely separates two
 * words the bigram pass should see.
 */
function verbForm(word: string): string {
  return word.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** The singular of a bare word, if stripping one trailing `s` is all it takes. */
function singular(bare: string): string {
  return bare.endsWith('s') ? bare.slice(0, -1) : bare;
}

/**
 * The department one word — or one space-separated phrase, which is how the multi-word entries
 * are keyed — suggests, or `undefined`.
 *
 * Normalisation is deliberately shallow — lowercase, strip surrounding punctuation, strip one
 * trailing `s`. No stemmer, because a stemmer is where a table like this starts matching words
 * it was never given ("parking" → "park"), which is the failure mode §7.2 objection 2 is about.
 *
 * An ambiguous unigram (rule 2) returns `undefined` here because it is not in the table at all.
 * That absence IS the rule: there is no separate check to forget to call.
 */
export function departmentForWord(word: string, locale = 'en'): ShoppingDepartment | undefined {
  const table = shoppingWords(locale);
  const bare = bareWord(word);
  if (!bare) return undefined;
  return table.get(bare) ?? table.get(singular(bare));
}

/** Rule 3, applied to one already-bare word. Plurals veto too: "bolts" is `bolt`. */
function isVetoed(bare: string): boolean {
  return FARM_DIY_VETO_EN.has(bare) || FARM_DIY_VETO_EN.has(singular(bare));
}

/**
 * The department a captured line suggests, and the words that suggested it — or `undefined`,
 * which is the answer this function is designed to give most often.
 *
 * The four rules in the file header, in the order they are applied. Read that header first; the
 * order is load-bearing, because rule 1 and rule 3 exist to stop rules 2 and 4 ever being asked.
 *
 *   1. TASK CHECK (product-concept §7.2 objection 2). Head word is an imperative verb and the
 *      line has more words → this is a loop, not an item. Silence.
 *   2. VETO CHECK (rule 3). Any farm/DIY word anywhere in the line → silence.
 *   3. LONGEST N-GRAM FIRST. Three adjacent words joined and looked up, then two, then one, each
 *      pass left to right and non-overlapping; a match consumes all of its words. Because the
 *      passes are SEPARATE, "the longer match wins" is a property of the algorithm and not of the
 *      order words happen to appear in — the old single-pass version only got this right by
 *      accident of its loop shape, and the critique's "chicken feed" → `meat-fish` was that
 *      accident failing. Three words is the longest entry in the table ("washing up liquid").
 *   4. DISAGREEMENT IS SILENCE. Two departments matched → this is a shopping TRIP, and naming
 *      one of its aisles would be a confident wrong guess. Words agreeing on one department are
 *      a stronger signal rather than a conflict, so that is allowed and the caller prices it
 *      higher (RULINGS R31).
 *
 * Punctuation between two words is a BARRIER: "Re-hang the shed door — hinge is bent" must never
 * form the pair "door hinge", because a pair that spans a dash is two thoughts, not one item.
 *
 * The caller owns the confidence and the proposal (RULINGS R31); this only reports the match.
 */
export function departmentForLine(
  line: string,
  locale = 'en',
): { readonly department: ShoppingDepartment; readonly matched: readonly string[] } | undefined {
  const tokens = line.split(/\s+/).filter(Boolean);
  const bare = tokens.map(bareWord);
  // A token that is pure punctuation, or that ends in punctuation, closes the phrase after it.
  const barrierAfter = tokens.map((token, i) => bare[i] === '' || /[^a-z0-9]$/i.test(token));

  const realWords = bare.filter((word) => word !== '');
  if (realWords.length === 0) return undefined;

  // --- Rule 1: a task is not an item. -------------------------------------------------------
  const head = verbForm(realWords[0] ?? '');
  if (realWords.length > 1 && (TASK_VERBS_EN.has(head) || DUAL_HEAD_VERBS_EN.has(head))) {
    return undefined;
  }

  // --- Rule 3: the farm/DIY veto, before anything is matched. --------------------------------
  if (realWords.some(isVetoed)) return undefined;

  const consumed = bare.map(() => false);
  const matched: string[] = [];
  let found: ShoppingDepartment | undefined;

  /** Record a match, or bail out on disagreement. Returns false when the line must be silent. */
  const record = (department: ShoppingDepartment, words: string): boolean => {
    if (found !== undefined && found !== department) return false;
    found = department;
    matched.push(words);
    return true;
  };

  // --- Rule 4: longest n-gram first, one pass per length. ------------------------------------
  for (let size = LONGEST_PHRASE; size >= 1; size -= 1) {
    for (let i = 0; i + size <= bare.length; i += 1) {
      const span = bare.slice(i, i + size);
      // A span is usable only if every word in it is a real word, none is already spoken for,
      // and no punctuation barrier falls between any two of them.
      if (span.some((word, offset) => word === '' || consumed[i + offset])) continue;
      if (span.slice(0, -1).some((_, offset) => barrierAfter[i + offset])) continue;
      const department = departmentForWord(span.join(' '), locale);
      if (department === undefined) continue;
      if (!record(department, span.join(' '))) return undefined;
      for (let offset = 0; offset < size; offset += 1) consumed[i + offset] = true;
    }
  }

  return found === undefined ? undefined : { department: found, matched };
}
